/**
 * Route guards (FE-01 FR-FE01-04, §6.2). `decide` is the pure decision table; `guard` runs it in
 * `beforeLoad` with the cached `me` query, so protected UI never renders before the check.
 */
import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

import { type Me, areaOf, isAdmin, meQuery } from "./me";

export type GuardKind = "seller" | "admin" | "owner" | "guestSeller" | "guestOwner";
export type LoginNotice = "expired" | "sellersOnly" | "ownersOnly";

export const LOGIN_NOTICES: readonly LoginNotice[] = ["expired", "sellersOnly", "ownersOnly"];

export type Decision =
  | { kind: "render" }
  | { kind: "forbidden" }
  | {
      kind: "redirect";
      to: "/app/login" | "/portal/login" | "/app" | "/portal";
      redirect?: string;
      notice?: LoginNotice;
    };

/** `here` is the current relative path + search, kept as `redirect` on login redirects. */
export function decide(me: Me | null, kind: GuardKind, here: string): Decision {
  const area = me ? areaOf(me) : null;
  switch (kind) {
    case "seller":
    case "admin":
      if (!me) return { kind: "redirect", to: "/app/login", redirect: here };
      if (area === "owner") return { kind: "redirect", to: "/app/login", notice: "sellersOnly" };
      if (kind === "admin" && !isAdmin(me)) return { kind: "forbidden" };
      return { kind: "render" };
    case "owner":
      if (!me) return { kind: "redirect", to: "/portal/login", redirect: here };
      if (area !== "owner") return { kind: "redirect", to: "/portal/login", notice: "ownersOnly" };
      return { kind: "render" };
    // A session of the other area may still open this login (to switch roles): no loop.
    case "guestSeller":
      return area === "seller" ? { kind: "redirect", to: "/app" } : { kind: "render" };
    case "guestOwner":
      return area === "owner" ? { kind: "redirect", to: "/portal" } : { kind: "render" };
  }
}

/** Thrown for the admin "no access" state (FR-FE01-21); the route's error component renders it. */
export class ForbiddenError extends Error {
  constructor() {
    super("forbidden");
    this.name = "ForbiddenError";
  }
}

interface GuardArgs {
  context: { queryClient: QueryClient };
  location: { pathname: string; searchStr: string };
}

export async function guard(kind: GuardKind, { context, location }: GuardArgs): Promise<Me | null> {
  const me = await context.queryClient.ensureQueryData(meQuery);
  const decision = decide(me, kind, `${location.pathname}${location.searchStr}`);
  if (decision.kind === "forbidden") throw new ForbiddenError();
  if (decision.kind === "redirect") {
    if (decision.to === "/app" || decision.to === "/portal") {
      throw redirect({ to: decision.to, replace: true });
    }
    throw redirect({
      to: decision.to,
      search: { redirect: decision.redirect, notice: decision.notice },
      replace: true,
    });
  }
  return me;
}
